import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import type { AccountInfo } from '@azure/msal-browser';
import { tenantLabelFor } from './active-account';

/**
 * What the app can honestly say about the session right now.
 *
 * WHY THIS EXISTS. Every auth failure in this app so far has presented as a
 * mystery: a keyless request that came back 401 and read as a server fault; a
 * token acquisition anchored to the wrong identity that produced "we couldn't
 * confirm your access just now"; a recovery redirect latched shut so the app
 * quietly stopped trying. In each case the client KNEW what was wrong and only
 * said so in the console. Nobody reads the console.
 *
 * `signed-in` is the only state where data calls can be expected to work.
 * The other three are all "not working", but they need different actions from
 * the user, and collapsing them into one "logged out" is what made the
 * difference invisible.
 */
export type AuthStatusKind =
  /** No account. Sign in. */
  | 'signed-out'
  /** Anchored to a known account and holding a usable token. */
  | 'signed-in'
  /**
   * More than one signed-in account and no record of which was chosen.
   * NOT signed out — the accounts are there — and NOT usable, because acting as
   * a guessed one is the defect this whole area was built to stop.
   */
  | 'account-ambiguous'
  /**
   * We know who the user is; MSAL will not give us a token for them. A genuine
   * expiry, a revoked grant, or a cache that cannot be decrypted after a browser
   * restart. Signing in again is what fixes it — and saying so is the entire
   * point, because this is the state that used to look like a server outage.
   */
  | 'token-refresh-failed';

export interface AuthStatus {
  kind: AuthStatusKind;
  /** The anchored account, when there is one. */
  account: AccountInfo | null;
  /** Shown next to the name: which tenant this identity belongs to. */
  tenant: string;
  /** One human sentence. Displayed verbatim — keep it actionable. */
  message: string;
  /** Candidate usernames, for the ambiguous case only. */
  candidates: string[];
}

/**
 * The state to assume before anything has been established.
 *
 * Exported so a component can initialise its field from a CONSTANT rather than by
 * reading a service at field-initialiser time. That ordering matters: field
 * initialisers run before the constructor body, so a component built positionally
 * — as several specs do — would dereference an undefined dependency and throw
 * during construction.
 */
export const INITIAL_AUTH_STATUS: AuthStatus = {
  kind: 'signed-out',
  account: null,
  tenant: '',
  message: 'Not signed in.',
  candidates: [],
};

/**
 * Single source of truth for "is this session actually working".
 *
 * Written to from the two places that genuinely know: UserAuthService, which sees
 * sign-in and account resolution, and VericureAuthInterceptor, which is the only
 * thing that finds out a token cannot be refreshed.
 */
@Injectable({ providedIn: 'root' })
export class AuthStatusService {
  private readonly _status = new BehaviorSubject<AuthStatus>(INITIAL_AUTH_STATUS);
  readonly status$: Observable<AuthStatus> = this._status.asObservable();

  /** Convenience for templates that only care whether things are healthy. */
  readonly healthy$: Observable<boolean> = this.status$.pipe(
    map((s) => s.kind === 'signed-in'),
    distinctUntilChanged()
  );

  get snapshot(): AuthStatus {
    return this._status.getValue();
  }

  signedIn(account: AccountInfo): void {
    this.push({
      kind: 'signed-in',
      account,
      tenant: tenantLabelFor(account),
      message: `Signed in as ${account.username}.`,
      candidates: [],
    });
  }

  signedOut(): void {
    this.push(INITIAL_AUTH_STATUS);
  }

  ambiguous(candidates: AccountInfo[]): void {
    this.push({
      kind: 'account-ambiguous',
      account: null,
      tenant: '',
      message:
        `${candidates.length} accounts are signed in and none is selected. ` +
        `Choose one to continue.`,
      candidates: candidates.map((a) => a.username),
    });
  }

  /**
   * Called when a token could not be refreshed for an account we DO know.
   *
   * Keeps the account, because "we know who you are and cannot get you a token"
   * is a materially different message from "sign in", and the user should still
   * see whose session went stale — particularly with more than one to choose from.
   */
  tokenRefreshFailed(account: AccountInfo | null, reason?: string): void {
    const who = account?.username;
    this.push({
      kind: 'token-refresh-failed',
      account,
      tenant: tenantLabelFor(account),
      message:
        (who
          ? `Your session for ${who} has expired or cannot be renewed.`
          : `Your session has expired or cannot be renewed.`) +
        ` Sign in again to continue.` +
        (reason ? ` (${reason})` : ''),
      candidates: [],
    });
  }

  /**
   * Only emit real changes.
   *
   * The interceptor reports per request, so a page load with a stale token would
   * otherwise republish the same failure a dozen times and make anything bound to
   * this flicker.
   */
  private push(next: AuthStatus): void {
    const prev = this._status.getValue();
    if (
      prev.kind === next.kind &&
      prev.account?.homeAccountId === next.account?.homeAccountId &&
      prev.message === next.message
    ) {
      return;
    }
    this._status.next(next);
  }
}
